import {computed, ref, watch} from 'vue';
import {gql} from '@apollo/client/core';
import {executeQuery} from '@/components/commons/utils.js';
import Tracking from '@/components/commons/Tracking.js';

const HELP_CENTER_PAGES_QUERY = gql`
  query HELP_CENTER_PAGES {
    curatedPagesByCategory(contentCategory: HELP_CENTER, activePagesOnly: true) {
      curatedPage {
        key {
          hashKey
        }
        title
        slug
        featureImage
        attributes {
          anchors
          inPopularTopic
        }
      }
    }
  }
`;

const PAGE_QUERY = gql`
  query HELP_CENTER_PAGE($slug: String, $tag: String) {
    curatedPagesByCategorySlugAndTag(contentCategory: HELP_CENTER, slug: $slug, tag: $tag) {
      curatedPage {
        title
        slug
        featureImage
        htmlContent
        seoTitle
        seoDescription
        attributes {
          anchors
          overrideMessageLink
        }
      }
    }
  }
`;

const HELP_CENTER_SUGGESTION_QUERY = gql`
  query HELP_CENTER_SUGGESTION($query: String) {
    searchHelpCenterSuggestion(query: $query) {
      id
      title
      slug
      content
      numMatchedWords
      numTypos
    }
  }
`;

const pages = ref([]);
const currentTopic = ref({});
const contactFormCustomLink = ref(null);

export default function useHelpCenter() {
  async function fetchInnerNavData() {
    try {
      const result = await executeQuery(HELP_CENTER_PAGES_QUERY);
      pages.value = result.curatedPagesByCategory?.curatedPage ?? [];
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  async function fetchPage({ slug, tag = 'latest' }) {
    try {
      if (!slug) {
        return;
      }

      const result = await executeQuery(PAGE_QUERY, {
        slug,
        tag,
      });

      currentTopic.value = result.curatedPagesByCategorySlugAndTag?.curatedPage?.[0] || {};

      if (Object.keys(currentTopic.value).length === 0) {
        return;
      }

      const searchQuery = getSearchQuery();
      const anchors = getPageAnchors(slug);
      const htmlContent = result.curatedPagesByCategorySlugAndTag?.curatedPage?.[0].htmlContent;

      window.location.hash = getHash(searchQuery, anchors, htmlContent);

    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  }

  function getSearchQuery() {
    const params = new URLSearchParams(window.location.search);
    const encodedQuery = params.has('query') ? params.get('query') : '';

    if (!encodedQuery) {
      return '';
    }

    const query = decodeURIComponent(encodedQuery);

    return query.substring(1);
  }

  // find the correct page and return its anchors
  function getPageAnchors(slug) {
    const page = pages.value.find(pg => pg.slug === slug);

    return page.attributes.anchors;
  }

  function getHash(searchQuery, anchors, htmlContent) {
    if (searchQuery.length === 0 || Object.keys(anchors).length === 0) {
      return '';
    }

    for (let anchor in anchors) {
      if (searchQuery === anchor || anchors[anchor].toLowerCase().includes(searchQuery.toLowerCase())) {
        return anchor;
      }
    }

    return getAnchorClosestToQuery(searchQuery, anchors, htmlContent);
  }

  function getAnchorClosestToQuery(searchQuery, anchors, htmlContent) {
    const anchorMap = mapAnchorNamesToIndices(anchors, htmlContent);
    const anchorNames = Object.keys(anchorMap);
    const anchorIndices = Object.values(anchorMap);

    const queryIndex = htmlContent.toLowerCase().search(new RegExp(searchQuery.toLowerCase(), "i"));
    const closestAnchorIndex = getClosestAnchorIndex(anchorIndices, queryIndex);

    return anchorNames.find(anchorIndex => anchorMap[anchorIndex] === closestAnchorIndex);
  }

  function mapAnchorNamesToIndices(anchors, htmlContent) {
    const anchorMap = {};
    for (let anchor in anchors) {
      anchorMap[anchor] = htmlContent.toLowerCase().search(anchors[anchor].toLowerCase());
    }
    return anchorMap;
  }

  function getClosestAnchorIndex(anchorIndices, queryIndex) {
    anchorIndices.sort((a, b) => a - b);

    for (let i = anchorIndices.length-1; i >= 0; i--) {
      if (queryIndex >= anchorIndices[i]) {
        return anchorIndices[i];
      }
    }

    return anchorIndices[0];
  }

  const popularTopics = computed(() =>
    pages.value
      .filter((page) => page.attributes.inPopularTopic)
      .map((page) => ({ title: page.title, slug: page.slug, featureImage: page.featureImage })),
  );

  async function getHelpCenterSuggestion(query = '') {
    try {
      const { searchHelpCenterSuggestion } = await executeQuery(HELP_CENTER_SUGGESTION_QUERY, {
        query,
      });

      return searchHelpCenterSuggestion;
    } catch (error) {
      console.error(error);
      Tracking.sentryCaptureError(error);
    }
  }

  watch(currentTopic, () => {
    contactFormCustomLink.value = currentTopic.value.attributes?.overrideMessageLink;
  });

  return {
    fetchInnerNavData,
    fetchPage,
    pages: computed(() => pages.value),
    currentTopic,
    contactFormCustomLink: computed(() => contactFormCustomLink.value),
    popularTopics,
    getHelpCenterSuggestion,
  };
}
